"use client";

import { useState, useEffect } from "react";
import { parseBerlinDate, startOfTodayBerlin } from "@/lib/utils/berlinDate";
import { getNextOccurrence } from "@/lib/utils/recurrence";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { storyblokEditable } from "@storyblok/react/rsc";
import {
  fetchEventsAndTravels,
  fetchStoryblokStory,
} from "@/lib/storyblok/fetchList";

import { EventCard } from "@/components/layout/EventCard";
import { GridContainer } from "@/components/layout/GridContainer";
import { RichTextField } from "@/components/typography/RichTextField";
import { Instagram, LinkedIn } from "@/components/icons";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";
import type { AssetStoryblok, MultilinkStoryblok, EventStoryblok, EventDateStoryblok } from "@/component-types-sb";
import { eventTypeFromContent } from "@/lib/utils/eventType";

type ExpertStoryblok = {
  name: string;
  title?: string;
  text?: string;
  image?: AssetStoryblok;
  instagram_link?: MultilinkStoryblok;
  linkedin_link?: MultilinkStoryblok;
  _uid: string;
  component: "expert";
  [k: string]: any;
};

type ExpertProps = {
  blok: ExpertStoryblok;
};

type EventData = {
  eventType: string;
  memberOnly: boolean;
  date: Date;
  title: string;
  ctaLink: string;
};

const Expert = ({ blok }: ExpertProps) => {
  const instagramHref = getUrlFromLinkObject(blok.instagram_link);
  const linkedinHref = getUrlFromLinkObject(blok.linkedin_link);
  const hasLinks = instagramHref || linkedinHref;
  const pathname = usePathname();
  const [events, setEvents] = useState<EventData[]>([]);

  useEffect(() => {
    if (!pathname) return;

    const fetchEvents = async () => {
      const slug = pathname.replace(/^\//, "");

      try {
        // First get the story UUID for this expert page
        const story = await fetchStoryblokStory<{ uuid?: string }>(slug);

        const storyUuid = story?.uuid;
        if (!storyUuid) return;

        // Alle Events UND Reisen, die auf diesen Experten verweisen
        const stories = await fetchEventsAndTravels({
          filter_query: {
            experts: { in_array: storyUuid },
          },
          sort_by: "content.date:asc",
          per_page: 100,
        });

        if (!stories.length) return;

        const cutoff = startOfTodayBerlin();
        const result: EventData[] = [];

        for (const story of stories) {
          const content = story.content as EventStoryblok;

          // For recurring events, find the next upcoming date
          if (content.repeating_dates?.length) {
            const nextDate = getNextOccurrence(content.repeating_dates as EventDateStoryblok[]);
            if (nextDate) {
              result.push({
                eventType: eventTypeFromContent(content),
                memberOnly: (content as { member_access?: string }).member_access === "member_only",
                date: nextDate,
                title: content.title,
                ctaLink: `/${story.full_slug}`,
              });
            }
            continue;
          }

          // Single event — check if it's in the future
          if (content.date) {
            const date = parseBerlinDate(content.date);
            if (date >= cutoff) {
              result.push({
                eventType: eventTypeFromContent(content),
                memberOnly: (content as { member_access?: string }).member_access === "member_only",
                date,
                title: content.title,
                ctaLink: `/${story.full_slug}`,
              });
            }
          }
        }

        result.sort((a, b) => a.date.getTime() - b.date.getTime());
        setEvents(result);
      } catch (error) {
        console.error("Error fetching events for expert:", error);
      }
    };

    fetchEvents();
  }, [pathname]);

  return (
    <section className="py-12" {...storyblokEditable(blok)}>
      <GridContainer>
        <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3">
          <div className="bg-sand-10 rounded-3xl p-6 flex flex-col gap-6 w-full">
            <div className="flex flex-col gap-6 md:flex-row md:gap-12 items-center md:items-start">
              {blok.image?.filename && (
                <div className="relative w-full md:w-[200px] aspect-[345/155] md:aspect-square rounded-2xl overflow-hidden shrink-0">
                  <Image
                    src={blok.image.filename}
                    alt=""
                    fill
                    sizes="(max-width: 768px) 100vw, 200px"
                    className="object-cover blur-xl scale-110 md:hidden"
                    aria-hidden="true"
                  />
                  <Image
                    src={blok.image.filename}
                    alt={blok.image.alt || blok.name || "Expert"}
                    fill
                    sizes="(max-width: 768px) 100vw, 200px"
                    className="object-contain md:object-cover"
                  />
                </div>
              )}
              <div className="flex flex-col gap-4 items-center md:items-start text-center md:text-left flex-1">
                <div className="flex flex-col text-black">
                  {blok.name && <h3 className="text-headline-xl">{blok.name}</h3>}
                  {blok.title && <p className="text-copy">{blok.title}</p>}
                </div>
                {blok.text && <RichTextField value={blok.text} className="text-copy text-black" />}
                {hasLinks && (
                  <div className="flex flex-col gap-2 items-center md:items-start">
                    <span className="text-copy-sm text-gray-60">Kontakt</span>
                    <div className="flex gap-2">
                      {instagramHref && (
                        <Link href={instagramHref} target="_blank" rel="noopener noreferrer">
                          <Instagram className="size-6 text-black" />
                        </Link>
                      )}
                      {linkedinHref && (
                        <Link href={linkedinHref} target="_blank" rel="noopener noreferrer">
                          <LinkedIn className="size-6 text-black" />
                        </Link>
                      )}
                    </div>
                  </div>
                )}
                {/* Events */}
                {events.length > 0 && (
                  <div className="flex flex-col gap-2 w-full">
                    <h3 className="text-headline-md text-black">
                      {events.length === 1 ? "Nächstes Event" : "Nächste Events"}
                    </h3>
                    {events.map((event) => (
                      <EventCard
                        key={event.ctaLink}
                        variant="densed"
                        eventType={event.eventType}
                        memberOnly={event.memberOnly}
                        date={event.date}
                        title={event.title}
                        ctaLink={event.ctaLink}
                        className="max-w-none"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </GridContainer>
    </section>
  );
};

export default Expert;
